/* eslint-disable @typescript-eslint/no-invalid-void-type */
import { apiSlice } from './apiSlice';
import Buyer from '../../core/models/Buyer';
import Seller from '../../core/models/Seller';
import AuctionRequest from '../../core/models/AuctionRequest';

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSellers: builder.query<Seller[], void>({
      query: () => '/sellers',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Seller' as const, id })),
              { type: 'Seller', id: 'LIST' },
            ]
          : [{ type: 'Seller', id: 'LIST' }],
    }),
    getSeller: builder.query<Seller, number>({
      query: (id) => `/sellers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Seller', id }],
    }),
    getPendingSellers: builder.query<Seller[], void>({
      query: () => '/sellers/pendingApproval',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PendingSeller' as const, id })),
              { type: 'PendingSeller', id: 'LIST' },
            ]
          : [{ type: 'PendingSeller', id: 'LIST' }],
    }),
    updateSeller: builder.mutation<Seller, Seller>({
      query: (data) => ({
        url: '/sellers/admin',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Seller'],
    }),
    approveSeller: builder.mutation<Seller, { id: string }>({
      query: (args) => ({
        url: '/sellers/approve',
        method: 'PUT',
        body: { id: args.id },
      }),
      invalidatesTags: ['Seller', 'PendingSeller'],
    }),
    blockSeller: builder.mutation<Seller, { id: string }>({
      query: (args) => ({
        url: '/sellers/block',
        method: 'PUT',
        body: { id: args.id },
      }),
      invalidatesTags: ['Seller', 'PendingSeller'],
    }),
    enableSeller: builder.mutation<Seller, string>({
      query: (sellerId) => ({
        url: '/accounts/enable',
        method: 'PUT',
        body: { accountId: sellerId, role: 2 },
      }),
      invalidatesTags: ['Seller', 'Seller'],
    }),
    disableSeller: builder.mutation<Seller, string>({
      query: (sellerId) => ({
        url: '/accounts/disable',
        method: 'PUT',
        body: { accountId: sellerId, role: 2 },
      }),
      invalidatesTags: ['Seller', 'Seller'],
    }),
    deleteSeller: builder.mutation<Seller, string>({
      query: (sellerId) => ({
        url: `/sellers/${sellerId}`,
        method: 'DElETE',
      }),
      invalidatesTags: ['Seller', 'Seller'],
    }),
    getBuyers: builder.query<Buyer[], void>({
      query: () => '/buyers',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Buyer' as const, id })),
              { type: 'Buyer', id: 'LIST' },
            ]
          : [{ type: 'Buyer', id: 'LIST' }],
    }),
    getBuyer: builder.query<Buyer, number>({
      query: (id) => `/buyers/${id}`,
      providesTags: (result, error, id) => [{ type: 'Buyer', id }],
    }),
    getPendingBuyers: builder.query<Buyer[], void>({
      query: () => '/buyers/pendingApproval',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PendingBuyer' as const, id })),
              { type: 'PendingBuyer', id: 'LIST' },
            ]
          : [{ type: 'PendingBuyer', id: 'LIST' }],
    }),
    updateBuyer: builder.mutation<Buyer, Buyer>({
      query: (data) => ({
        url: '/buyers/admin',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Buyer'],
    }),
    approveBuyer: builder.mutation<Buyer, { id: string }>({
      query: (args) => ({
        url: '/buyers/approve',
        method: 'PUT',
        body: { id: args.id },
      }),
      invalidatesTags: ['Buyer', 'PendingBuyer'],
    }),
    blockBuyer: builder.mutation<Buyer, { id: string }>({
      query: (args) => ({
        url: '/buyers/block',
        method: 'PUT',
        body: { id: args.id },
      }),
      invalidatesTags: ['Buyer', 'PendingBuyer'],
    }),
    enableBuyer: builder.mutation<Buyer, string>({
      query: (buyerId) => ({
        url: '/accounts/enable',
        method: 'PUT',
        body: { accountId: buyerId, role: 3 },
      }),
      invalidatesTags: ['Buyer', 'Buyer'],
    }),
    disableBuyer: builder.mutation<Buyer, string>({
      query: (buyerId) => ({
        url: '/accounts/disable',
        method: 'PUT',
        body: { accountId: buyerId, role: 3 },
      }),
      invalidatesTags: ['Buyer', 'Buyer'],
    }),
    deleteBuyer: builder.mutation<Buyer, string>({
      query: (buyerId) => ({
        url: `/buyers/${buyerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Buyer', 'Buyer'],
    }),
    changePassword: builder.mutation<any, string>({
      query: (data) => ({
        url: '/accounts/password',
        method: 'PUT',
        body: data,
      }),
    }),
    getPendingBidders: builder.query<AuctionRequest[], void>({
      query: () => '/auctions/bidding/request/pending',
      keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'PendingBidders' as const, id })),
              { type: 'PendingBidders', id: 'LIST' },
            ]
          : [{ type: 'PendingBidders', id: 'LIST' }],
    }),
    approveBidder: builder.mutation<void, { id: string }>({
      query: (args) => ({
        url: '/auctions/bidding/request/approve',
        method: 'PUT',
        body: { bidderId: args.id },
      }),
      invalidatesTags: ['Buyer', 'PendingBidders'],
    }),
    rejectBidder: builder.mutation<void, { id: string }>({
      query: (args) => ({
        url: '/auctions/bidding/request/reject',
        method: 'PUT',
        body: { bidderId: args.id },
      }),
      invalidatesTags: ['Buyer', 'PendingBidders'],
    }),
  }),
});

export const {
  useGetSellersQuery,
  useGetSellerQuery,
  useGetPendingSellersQuery,
  useUpdateSellerMutation,
  useApproveSellerMutation,
  useBlockSellerMutation,
  useEnableSellerMutation,
  useDisableSellerMutation,
  useDeleteSellerMutation,
  useGetBuyersQuery,
  useGetBuyerQuery,
  useGetPendingBuyersQuery,
  useUpdateBuyerMutation,
  useApproveBuyerMutation,
  useBlockBuyerMutation,
  useEnableBuyerMutation,
  useDisableBuyerMutation,
  useDeleteBuyerMutation,
  useGetPendingBiddersQuery,
  useApproveBidderMutation,
  useRejectBidderMutation,
  useChangePasswordMutation,
} = userApiSlice;
